import React, { useEffect, useState } from "react";
import {
    Paper,
    Skeleton,
    Typography,
    Box,
    Chip,
    Divider,
    Button,
    Grid,
    Link,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import CategoryIcon from '@mui/icons-material/Category';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useGovernmentProjectsController } from "./context/government-projects-controller";
import { useGovernmentProjectsState } from "./context/government-projects-context";
import PageHeader from "../components/shared/page-header";
import { useParams } from "react-router-dom";
import { useNavigate } from "../../hooks/useNavigate";

export function GovernmentProjectDetailComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const { loadDetail } = useGovernmentProjectsController();
    const { projectDetail } = useGovernmentProjectsState();
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            if (id) {
                await loadDetail(Number(id));
                setInitialLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const handleBack = () => {
        navigate('/projetos-governamentais');
    };

    return (
        <Box>
            <PageHeader
                title={projectDetail?.projectName || 'Carregando...'}
                subtitle=""
                breadcrumbs={[
                    { label: 'Projetos Governamentais', href: '/projetos-governamentais' },
                    { label: projectDetail?.projectName || 'Carregando...' }
                ]}
            />

            {initialLoading ? (
                <Box>
                    <Skeleton variant="rounded" width="100%" height={400} sx={{ borderRadius: 2, mb: 3 }} />
                    <Skeleton variant="text" width="60%" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="80%" height={30} />
                </Box>
            ) : (
                <React.Fragment>
                    <Paper sx={{ p: 4, mb: 3 }} elevation={2}>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                            {projectDetail?.projectName}
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <AccountBoxIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Responsável
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {projectDetail?.responsibleName || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <CategoryIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Tipo de Projeto
                                        </Typography>
                                        <Chip
                                            label={projectDetail?.projectTypeDescription || 'Não especificado'}
                                            color="primary"
                                            size="medium"
                                            variant="outlined"
                                            sx={{ mt: 0.5 }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>

                            {projectDetail?.results && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                                        <AssignmentIcon color="primary" />
                                        <Typography variant="h6" fontWeight={600}>
                                            Resultados
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            lineHeight: 1.7,
                                            whiteSpace: 'pre-wrap'
                                        }}
                                    >
                                        {projectDetail.results}
                                    </Typography>
                                </Grid>
                            )}

                            {projectDetail?.link && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', mt: 2 }}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            endIcon={<OpenInNewIcon />}
                                            component={Link}
                                            href={projectDetail.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ textDecoration: 'none' }}
                                        >
                                            Acessar Link Externo do Projeto
                                        </Button>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>

                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            size="large"
                        >
                            Voltar para Projetos Governamentais
                        </Button>
                    </Box>
                </React.Fragment>
            )}
        </Box>
    );
}
